import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/ampa-tms-reviews-what-to-expect — PROMPT 6
 * Patient-facing reviews/what-to-expect page. FAQPage schema only — no
 * Review/AggregateRating schema for third-party stories (per rules).
 */

const AMPA_TRADEMARK =
  "Ampa and Ampa One are trademarks of Ampa Inc. Neuro Wellness TMS Centers of America is an independent provider and is not owned by Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "Does Ampa TMS hurt?",
    answer:
      "Most patients feel a tapping sensation on the scalp during stimulation, not pain. The most common side effect is mild scalp discomfort that usually fades as the session goes on. Ampa One sessions are short — a few minutes of stimulation — which can make them more comfortable than longer conventional sessions. No anesthesia is used, and you can drive home afterward.",
  },
  {
    question: "What are the side effects of Ampa One?",
    answer:
      "The most common side effects are mild scalp discomfort and headache that usually fade during or after the session. Less commonly, patients report lightheadedness. As with all TMS, there is a rare risk of seizure. TMS is not suitable for people with certain metal or electronic implants in or near the head, pacemakers/ICDs, or a seizure disorder. A physician evaluation decides if it is safe for you.",
  },
  {
    question: "How soon will I feel better?",
    answer:
      "Response varies. Some patients notice a change within the first week; for others it builds over the course. We measure response with the PHQ-9 depression scale before, during, and after treatment so progress is tracked, not guessed. Ampa has reported early, non-randomized results of 87% response and 72% remission at 6 weeks (NRx Pharmaceuticals press release, November 2025) — these are early results and individual results vary.",
  },
  {
    question: "Is Ampa TMS safe?",
    answer:
      "Ampa One is FDA-cleared for major depressive disorder in adults. TMS is non-invasive — no surgery, no anesthesia. The most common side effects are mild and temporary. The serious risks are rare and include seizure. Safety is screened before treatment: certain implants, pacemakers/ICDs, metal in the head or eyes, or a seizure disorder may make TMS unsafe for you. A physician evaluation makes the final decision.",
  },
  {
    question: "What do patients say about Ampa One?",
    answer:
      "Patients describe Ampa One sessions as quick, with a tapping feeling on the scalp and no anesthesia. Stories shared by Ampa, the device maker (at ampahealth.com/patients), are not Neuro Wellness patients and individual results vary. As we collect our own Google reviews that mention Ampa One, we add them here with patient permission.",
  },
];

export const pageAmpaTmsReviewsWhatToExpect = makeArticle({
  slug: "ampa-tms-reviews-what-to-expect",
  title:
    "Ampa One TMS Reviews & What to Expect: Sessions, Side Effects, Results",
  description:
    "What Ampa One TMS is really like: sessions, side effects, results timeline, patient stories from the device maker, and what to expect at our Coral Springs clinic.",
  canonical: "/learn/ampa-tms-reviews-what-to-expect",
  h1: "Ampa One TMS: Reviews, Side Effects and What Treatment Is Really Like",
  breadcrumbLabel: "Ampa One Reviews & What to Expect",
  shortAnswer:
    "Patients describe Ampa One sessions as quick (a few minutes of stimulation), with a tapping feeling on the scalp and no anesthesia. The most common side effects are mild scalp discomfort and headache that usually fade. Below are patient stories published by the maker, plus what to expect at our Coral Springs clinic. Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation.",
  keyTakeaways: [
    "Sessions are short — a few minutes of stimulation with a tapping feeling, no anesthesia.",
    "Most common side effects: mild scalp discomfort and headache that usually fade.",
    "Response is tracked with the PHQ-9 before, during, and after treatment.",
    "Ampa has reported early, non-randomized results: 87% response, 72% remission at 6 weeks (NRx press release, Nov 2025). Individual results vary.",
    "Patient stories shared by Ampa (the device maker) are not Neuro Wellness patients.",
    "You can drive home and return to work after a session.",
  ],
  sections: [
    {
      heading: "Quick answer",
      paragraphs: [
        "Ampa One sessions are quick — about a minute to set up and a few minutes of stimulation, with a tapping sound and feeling on the scalp and no anesthesia. The most common side effects are mild scalp discomfort and headache that usually fade. Below are patient stories published by the device maker, plus what to expect at our Coral Springs clinic.",
      ],
    },
    {
      heading: "Before your first session",
      paragraphs: [
        "Before your first session, you will have a screening to confirm Ampa One is safe and appropriate for you. Wear something comfortable, eat normally, and bring headphones and a laptop or book if you would like something for the rest breaks. Remove metal jewelry and accessories from the head and neck area before treatment.",
      ],
    },
    {
      heading: "Day one: cap fitting, mapping, first session",
      paragraphs: [
        "On day one, the technician fits the pre-printed neuronavigation cap to your head, then runs motor-threshold mapping to set your stimulation level. The camera built into the coil tracks the cap so every session stays on the same target. Your first session is a few minutes of stimulation with a tapping feeling. You can drive home and return to work afterward.",
      ],
    },
    {
      heading: "Side effects and safety",
      paragraphs: [
        "The most common side effects are mild scalp discomfort and headache that usually fade during or after the session. Less commonly, patients report lightheadedness. As with all TMS, there is a rare risk of seizure. TMS is not suitable for people with certain metal or electronic implants in or near the head (aneurysm clips, cochlear implants, implanted stimulators), pacemakers/ICDs, metal fragments in the head or eyes, or a seizure disorder. A physician evaluation makes the final decision.",
      ],
    },
    {
      heading: "Patient stories published by Ampa",
      paragraphs: [
        "Stories shared by Ampa, the device maker, are published at ampahealth.com/patients. These are Ampa's stories, not Neuro Wellness patients, and individual results vary. As we collect our own Google reviews that mention Ampa One, we add them here with patient permission. We do not use Review or AggregateRating schema for third-party stories.",
      ],
    },
    {
      heading: "How results are measured",
      paragraphs: [
        "We measure response with the PHQ-9 depression scale before, during, and after treatment, so progress is tracked, not guessed. Some patients notice a change within the first week; for others it builds over the course. Ampa has reported early, non-randomized results of 87% response and 72% remission at 6 weeks (NRx Pharmaceuticals press release, November 10, 2025) — these are early, non-randomized results and individual results vary.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Ampa Inc. Patient stories. ampahealth.com/patients (accessed November 3, 2026).",
      url: "https://www.ampahealth.com/patients",
    },
    {
      citation:
        "NRx Pharmaceuticals. Press release on Ampa One early results (November 10, 2025).",
    },
  ],
  relatedLinks: [
    {
      title: "Ampa One™ TMS in Florida",
      href: "/ampa",
      description: "Camera-guided, accelerated depression treatment",
    },
    {
      title: "One-Day TMS in Florida",
      href: "/one-day-tms",
      description: "The Ampa One Day protocol explained",
    },
    {
      title: "Ampa One TMS Cost",
      href: "/learn/ampa-tms-cost",
      description: "Price, insurance, and payment options",
    },
    {
      title: "SAINT® vs Ampa One",
      href: "/learn/saint-vs-ampa-one",
      description: "Two accelerated TMS options, one Florida clinic",
    },
    {
      title: "Schedule a Consultation",
      href: "/contactus",
      description: "Book a free consultation with our clinical team",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick answer", href: "#quick-answer" },
    { label: "Before your first session", href: "#before-your-first-session" },
    { label: "Day one", href: "#day-one-cap-fitting-mapping-first-session" },
    { label: "Side effects & safety", href: "#side-effects-and-safety" },
    { label: "Patient stories", href: "#patient-stories-published-by-ampa" },
    { label: "How results are measured", href: "#how-results-are-measured" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation. Common side effects include mild scalp discomfort and headache that usually fade. If you are in crisis, call or text 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "See if Ampa One is a fit, free",
    primaryText: "Request a free consultation",
    primaryHref: "/ampa",
    secondaryText: "Call 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
